import { PowerSyncContext, useQuery } from '@powersync/react';
import { Link, Navigate, Outlet, useMatchRoute } from '@tanstack/react-router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { queries, type WorkspaceSummary } from '@flow/sync';
import { AppShell, Sidebar, ViewHeader, type SidebarSection } from '@flow/ui/components';

import { QuickCapture } from '../capture/QuickCapture.jsx';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useSession, type SessionUser } from '../session.jsx';
import { SyncedTheme } from '../theme/ThemeControl.jsx';

import { CommandPalette } from './CommandPalette.jsx';
import { useCommands, type Command } from './commands.js';
import { Rejections } from './Rejections.jsx';
import { sidebarSections } from './sections.js';
import { ShortcutsSheet } from './ShortcutsSheet.jsx';
import { SidebarFooter } from './SidebarFooter.jsx';
import { useShellState } from './use-shell-state.js';
import { useShortcuts } from './use-shortcuts.js';
import { useSidebarData } from './use-sidebar-data.js';

/**
 * Authenticated layout (docs/05 §5.1): every signed-in screen renders inside it. It waits for the
 * session and the local database, builds the sidebar from the user's installed views and leaves
 * the content to the route. Each screen sets its own header through `useViewHeader`.
 */

interface HeaderState {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

const HeaderContext = createContext<((state: HeaderState) => void) | null>(null);
const DetailContext = createContext<((detail: ReactNode) => void) | null>(null);
const ScreenCommandsContext = createContext<((commands: Command[]) => void) | null>(null);

/** A screen declares its header; the layout paints it above the content. */
export function useSetViewHeader(): (state: HeaderState) => void {
  const setHeader = useContext(HeaderContext);
  if (!setHeader) throw new Error('useSetViewHeader necesita AppLayout');
  return setHeader;
}

/** A screen puts its detail panel in the shell's own slot (docs/05 §5.1). */
export function useSetDetail(): (detail: ReactNode) => void {
  const setDetail = useContext(DetailContext);
  if (!setDetail) throw new Error('useSetDetail necesita AppLayout');
  return setDetail;
}

/**
 * A screen adds its own commands to the palette while it is shown, e.g. «Convertir en…» with a
 * block open (f1-type-conversion D4). Pass a memoised list: every new list replaces the last.
 */
export function useScreenCommands(commands: Command[]): void {
  const setCommands = useContext(ScreenCommandsContext);
  if (!setCommands) throw new Error('useScreenCommands necesita AppLayout');
  useEffect(() => {
    setCommands(commands);
    return () => setCommands([]);
  }, [commands, setCommands]);
}

export function AppLayout() {
  const { status, user, local } = useSession();
  if (status === 'loading') return <p className="muted">{t.app.loading}</p>;
  if (status === 'anonymous' || !user) return <Navigate to="/login" />;
  if (status === 'opening' || !local) return <p className="muted">{t.app.openingDatabase}</p>;

  return (
    <PowerSyncContext.Provider value={local.db}>
      <Shell user={user} local={local} />
    </PowerSyncContext.Provider>
  );
}

function Shell({ user, local }: { user: SessionUser; local: LocalDatabase }) {
  const shell = useShellState();
  const [header, setHeader] = useState<HeaderState>({ title: t.app.title });
  const [detail, setDetail] = useState<ReactNode>(null);
  const [screenCommands, setScreenCommands] = useState<Command[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const matchRoute = useMatchRoute();

  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<WorkspaceSummary>(
    workspaceQuery.sql,
    workspaceQuery.params,
  );
  const workspace = workspaces[0];
  const { closeMenu } = shell;
  const openCapture = useCallback(() => {
    closeMenu();
    setCaptureOpen(true);
  }, [closeMenu]);
  const { views, rituals, badges, warningViews } = useSidebarData(user.id, workspace?.id ?? '');

  const sections = useMemo<SidebarSection[]>(
    () =>
      sidebarSections({
        views,
        rituals,
        badges,
        warningViews,
        isCurrent: (href) => matchRoute({ to: href }) !== false,
      }),
    [views, rituals, badges, warningViews, matchRoute],
  );

  const shellCommands = useCommands({
    views,
    openShortcuts: () => setShortcutsOpen(true),
    openCapture,
    toggleSidebar: shell.toggleCollapsed,
  });
  const commands = useMemo(
    () => [...screenCommands, ...shellCommands],
    [screenCommands, shellCommands],
  );
  useShortcuts({
    // The capture and the palette handle their own keys while open.
    enabled: !paletteOpen && !captureOpen,
    openPalette: () => setPaletteOpen(true),
    openCapture,
    openShortcuts: () => setShortcutsOpen(true),
    closeOverlays: () => {
      setShortcutsOpen(false);
      shell.closeMenu();
    },
  });

  return (
    <ScreenCommandsContext.Provider value={setScreenCommands}>
      <SyncedTheme userId={user.id} />
      <AppShell
        detail={detail}
        collapsed={shell.collapsed}
        menuOpen={shell.menuOpen}
        onCloseMenu={shell.closeMenu}
        navigationLabel={t.shell.navigation}
        closeMenuLabel={t.shell.closeMenu}
        sidebar={
          <Sidebar
            workspaceName={workspace?.name ?? t.shell.loadingWorkspace}
            sections={sections}
            collapsed={shell.collapsed}
            onToggleCollapsed={shell.toggleCollapsed}
            toggleLabel={shell.collapsed ? t.shell.expandSidebar : t.shell.collapseSidebar}
            linkComponent={({ href, children, className, current, title }) => (
              <Link
                to={href}
                className={className}
                title={title}
                aria-current={current ? 'page' : undefined}
                onClick={shell.closeMenu}
              >
                {children}
              </Link>
            )}
            footer={
              <SidebarFooter
                user={user}
                local={local}
                onOpenPalette={() => setPaletteOpen(true)}
                onOpenCapture={openCapture}
              />
            }
          />
        }
        header={
          <ViewHeader
            title={header.title}
            {...(header.subtitle === undefined ? {} : { subtitle: header.subtitle })}
            {...(header.actions === undefined ? {} : { actions: header.actions })}
            onOpenMenu={shell.openMenu}
            menuLabel={t.shell.openMenu}
            onCapture={openCapture}
            captureLabel={t.quickCapture.open}
          />
        }
      >
        <HeaderContext.Provider value={setHeader}>
          <DetailContext.Provider value={setDetail}>
            <Rejections local={local} />
            <Outlet />
          </DetailContext.Provider>
        </HeaderContext.Provider>
      </AppShell>
      <CommandPalette
        open={paletteOpen}
        commands={commands}
        onClose={() => setPaletteOpen(false)}
      />
      <ShortcutsSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {workspace ? (
        <QuickCapture
          open={captureOpen}
          onClose={() => setCaptureOpen(false)}
          local={local}
          userId={user.id}
          workspaceId={workspace.id}
        />
      ) : null}
    </ScreenCommandsContext.Provider>
  );
}
